import { memo } from 'react';
import type { ComposerCapabilityCategory } from '@/types/composer';
import { getSidebarIcon } from '@/components/sidebar/sidebar-icon-registry';
import { CapabilityCategoryPillLabel } from '@/components/chat/capability-category-pill-label';
import {
  getCapabilityMenuPillClassName,
  getCapabilityMenuTrayClassName,
} from '@/components/chat/chat-message-styles';
import { isCapabilityCategoryPillStreaming } from '@/lib/capability-menu-stream-events';
import { cn } from '@/lib/utils';

interface CapabilityCategoryPillTrayProps {
  categories: ComposerCapabilityCategory[];
  openCategoryId: string | null;
  variant: 'welcome' | 'inChat';
  disabled?: boolean;
  onToggleCategory: (categoryId: string, canOpen: boolean) => void;
}

/** Horizontal capability category pills — shared by welcome and in-chat strip layouts. */
export const CapabilityCategoryPillTray = memo(function CapabilityCategoryPillTray({
  categories,
  openCategoryId,
  variant,
  disabled = false,
  onToggleCategory,
}: CapabilityCategoryPillTrayProps) {
  const isInChat = variant === 'inChat';

  return (
    <div
      className={getCapabilityMenuTrayClassName(isInChat)}
      role="group"
      aria-label="Agent capabilities"
    >
      {categories.map((category, index) => {
        const Icon = getSidebarIcon(category.icon);
        const isOpen = openCategoryId === category.id;
        const canOpen = category.examples.length > 0 || Boolean(category.streaming);
        return (
          <button
            key={category.id}
            type="button"
            disabled={disabled || !canOpen}
            aria-expanded={isOpen}
            aria-haspopup="dialog"
            className={cn(
              getCapabilityMenuPillClassName({ isOpen }),
              disabled && 'opacity-60',
              isInChat && 'shrink-0'
            )}
            onClick={() => onToggleCategory(category.id, canOpen)}
          >
            <Icon
              className={cn('h-3.5 w-3.5 shrink-0', isOpen ? 'opacity-90' : 'opacity-50')}
              aria-hidden
            />
            <CapabilityCategoryPillLabel
              title={category.title}
              streaming={isCapabilityCategoryPillStreaming(category)}
              staggerIndex={index}
            />
          </button>
        );
      })}
    </div>
  );
});
